import React, { Component, Fragment } from 'react';
import { compose } from 'recompose';
import { MutationFunction } from 'react-apollo';
import { Typography } from '@material-ui/core';
import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core/styles';

import { Content, CreateReactionComponent, DeleteReactionComponent } from 'api';
import { withQueryParams, IWithQueryParams } from 'providers';
import { Error, Heart } from 'components';
import { createReactionResponse, deleteReactionResponse } from 'apollo/responses';

import { contentModel } from 'apollo/reducers';
import Text from './Text';

interface IReactionMutations {
  createReaction: MutationFunction;
  deleteReaction: MutationFunction;
}

interface IProps {
  contentId: Content['content_id'];
  reactions: Content['reactions'];
}

interface IPropsEnhanced extends IProps, IWithQueryParams, WithStyles<typeof styles> {}

interface IState {
  isLiked: boolean;
}

const styles = (theme: Theme) =>
  createStyles({
    text: {
      marginLeft: theme.spacing(1),
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(withQueryParams, withStyles(styles));

class Reactions extends Component<IPropsEnhanced, IState> {
  static getDerivedStateFromProps(nextProps: IPropsEnhanced, prevState: IState) {
    const isLiked = Reactions.isLiked(nextProps);

    if (isLiked !== prevState.isLiked) {
      return { isLiked };
    }

    return null;
  }

  static isLiked(props: IPropsEnhanced) {
    const { reactions } = props;

    if (!reactions) {
      return false;
    }

    return !!reactions.user_reactions.length;
  }

  state: IState = {
    isLiked: Reactions.isLiked(this.props),
  };

  handleClick = ({ createReaction, deleteReaction }: IReactionMutations) => () => {
    const { contentId, query, variables } = this.props;
    const { isLiked } = this.state;

    let optimisticResponse: object = createReactionResponse();
    let mutation = createReaction;

    if (isLiked) {
      optimisticResponse = deleteReactionResponse();
      mutation = deleteReaction;
    }

    return mutation({
      variables: {
        content_id: contentId,
      },
      optimisticResponse,
      update: (cache, result) => {
        contentModel({ contentId, cache, result, query, variables });
      },
    });
  };

  renderReactions(mutations: IReactionMutations) {
    const { classes, reactions } = this.props;
    const { isLiked } = this.state;

    return (
      <Fragment>
        <Heart active={isLiked} onClick={this.handleClick(mutations)} />
        <Typography display="inline" className={classes.text}>
          <Text reactions={reactions} isLiked={isLiked} />
        </Typography>
      </Fragment>
    );
  }

  render() {
    return (
      <CreateReactionComponent>
        {(createReaction, createOptions) => (
          <Fragment>
            <Error message={createOptions.error} />
            <DeleteReactionComponent>
              {(deleteReaction, deleteOptions) => (
                <Fragment>
                  <Error message={deleteOptions.error} />
                  {this.renderReactions({ createReaction, deleteReaction })}
                </Fragment>
              )}
            </DeleteReactionComponent>
          </Fragment>
        )}
      </CreateReactionComponent>
    );
  }
}

export default enhancers(Reactions);
